import type { OnInit } from '@angular/core';
import { Component, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { ThemeService } from '@echospaces-web-monorepo/es-shared-theme';

import { AuthService } from '@echospaces-web-monorepo/es-auth';
import { AppErrorComponent, EnvBadgeComponent } from '@echospaces-web-monorepo/es-ui-next';


import { environment } from '../environments/environment';

/**
 * Root shell component — bootstraps authentication and theme, renders routed content.
 *
 * Auth is a crucial dependency: if OIDC discovery / the Keycloak handshake
 * fails (identity provider down, network, misconfig), there is no usable app
 * to show. Rather than leave a blank screen, we catch that failure and render
 * a full-screen error page with a retry instead of the router outlet.
 */
@Component({
  selector: 'es-portal-root',
  imports: [RouterOutlet, AppErrorComponent, EnvBadgeComponent],
  template: `
    @if (startupError(); as detail) {
      <esn-app-error [detail]="detail" (retry)="retryStartup()" />
    } @else {
      <router-outlet />
    }
    <esn-env-badge [frontend]="frontend" />
  `,
})
export class AppComponent implements OnInit {
  private readonly auth = inject(AuthService);
  protected readonly theme = inject(ThemeService); // initializes dark mode

  /**
   * Frontend serving origin, shown by the corner env badge (hidden in prod).
   */
  protected readonly frontend = environment.source;

  /**
   * Technical message when a crucial bootstrap dependency (auth) failed; null while healthy.
   */
  protected readonly startupError = signal<string | null>(null);

  async ngOnInit() {
    await this.startup();
  }

  /**
   * Retry after a fatal startup error — re-runs the whole bootstrap from scratch.
   */
  protected retryStartup(): void {
    this.reloadApp();
  }

  /**
   * Boot the crucial dependencies; surface a fatal-error screen if auth can't start.
   */
  private async startup(): Promise<void> {
    this.startupError.set(null);
    try {
      await this.auth.init();
    } catch (err) {
      this.startupError.set(err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Indirection over the full page reload so it can be stubbed in tests.
   */
  protected reloadApp(): void {
    window.location.reload();
  }
}
